import {
  breakpointSizes,
  getMinBreakpoint,
  styleConstants,
} from '@paywithextend/components'
import { RefObject, useLayoutEffect, useState } from 'react'

import { CONDENSED_HERO_HEIGHT } from './cardDetailHeroConstants'

/**
 * The SM breakpoint (px). The fixed global header is `topNavSMUp` (80px) at SM
 * and up, `topNavXSMDown` (58px) below it.
 *
 * Read from the design system rather than written as a literal. The compact
 * bar's `top` and the Subnav's sticky `top` both branch in CSS on
 * `getBreakpointDown(XSM)`, which compiles to `max-width: 767px` — i.e. the SM
 * minimum minus one, the same boundary this `< SM` test applies. A JS threshold
 * cannot be a media query, so the split is still expressed twice, but taking the
 * number from `getMinBreakpoint` keeps this side in lockstep with the CSS side if
 * the SM token ever moves. Both are re-evaluated on resize (this one via
 * `measure()`, the CSS ones by the media query itself).
 *
 * Deliberately a function, not a module constant: ~40 suites replace
 * `@paywithextend/components` with a hand-picked `jest.mock` stub, so ANY
 * design-system read at module scope crashes every suite that transitively
 * imports this file — including ones that never render the hero. Keeping the read
 * lazy puts it at exactly the altitude of the `styleConstants.heights` reads
 * below, which have the same dependency and are already only reached when the
 * hero actually mounts.
 */
const smBreakpointPx = (): number => getMinBreakpoint(breakpointSizes.SM) ?? 768

const px = (value: string): number => Number(value.replace('px', ''))

/**
 * Height of the fixed global header at the current viewport — the static part of
 * the swap threshold and of the compact bar's `top` (the runtime-variable part is
 * `fixedElementPushdown`).
 */
export const globalHeaderHeight = (): number =>
  typeof window !== 'undefined' && window.innerWidth < smBreakpointPx()
    ? px(styleConstants.heights.topNavXSMDown)
    : px(styleConstants.heights.topNavSMUp)

/**
 * The full→compact hero swap trigger. `compactOpen` flips true once the full
 * hero's bottom edge crosses `fixedElementPushdown + globalHeaderHeight() +
 * CONDENSED_HERO_HEIGHT`.
 *
 * That last term is what keeps the handoff seamless, and it is not optional. The
 * Subnav sticks at `pushdown + header + CONDENSED_HERO_HEIGHT` whenever the hero
 * renders, and `position: sticky` pushes an element DOWN as soon as its natural
 * top would sit above its sticky `top`. The Subnav's natural top is the full
 * hero's bottom edge — so the moment the hero's bottom crosses this threshold is
 * exactly the moment the tab bar starts being held away from the global header.
 * Opening the bar on that same frame fills the space as it appears. Triggering
 * instead at `pushdown + header` (i.e. when the hero is fully under the global
 * header) would leave the space empty for CONDENSED_HERO_HEIGHT worth of scroll,
 * opening a gap between the hero and the tabs that grows to the full bar height
 * before the bar arrives to cover it.
 *
 * The prototype gets the same result structurally rather than arithmetically:
 * there the bar is `position: sticky` with `height: 0` until open, so the tabs
 * cannot be displaced before the bar exists. We are `position: fixed` (a sticky
 * child cannot pin inside the short `<Header>` band), which buys the pinning but
 * costs a permanently-applied Subnav offset — hence the explicit term here.
 *
 * We MEASURE the hero's bottom rather than guess a scroll offset (plan §2.4), so
 * the swap stays correct when a runtime banner changes `fixedElementPushdown`.
 * The threshold is recomputed on every measurement, so it also tracks the global
 * header's height flipping at the SM breakpoint on resize. This is pure DOM
 * measurement, so an rAF-throttled scroll/resize listener drives it, with an
 * IntersectionObserver as the primary trigger and a 250ms interval safety net for
 * the layout shifts neither fires on (mirrors the prototype). Every one of those
 * four sources goes through `schedule`, never `measure` directly, so concurrent
 * triggers coalesce into a single layout read per frame and the pending-frame
 * handle stays cancellable at cleanup. `setCompactOpen` with an unchanged value is
 * a no-op, so the safety net does not churn renders.
 */
export const useCompactHeroSwap = (
  fullHeroRef: RefObject<HTMLElement>,
  fixedElementPushdown: number,
): boolean => {
  const [compactOpen, setCompactOpen] = useState(false)

  // Layout effect (pre-paint): when the page loads already scrolled past the swap
  // point (scroll restoration, back-nav), the initial `measure()` commits the bar
  // as open before the browser paints, so the full hero never flashes for a frame.
  // Ongoing scroll/resize/observer handling is async by nature.
  useLayoutEffect(() => {
    const el = fullHeroRef.current
    if (!el) return undefined

    let frame = 0
    // Clears the handle it was scheduled under, so the next `schedule` starts a
    // fresh frame. Only ever invoked BY a frame, or by the pre-paint mount call
    // below — which runs before any listener is attached, so it can never clobber
    // a handle that cleanup still needs to cancel.
    const measure = (): void => {
      frame = 0
      const threshold =
        fixedElementPushdown + globalHeaderHeight() + CONDENSED_HERO_HEIGHT
      setCompactOpen(el.getBoundingClientRect().bottom <= threshold)
    }
    const schedule = (): void => {
      if (frame === 0) frame = requestAnimationFrame(measure)
    }

    measure()
    window.addEventListener('scroll', schedule, { passive: true })
    window.addEventListener('resize', schedule)

    const observer =
      typeof IntersectionObserver !== 'undefined'
        ? new IntersectionObserver(schedule, { threshold: [0, 0.01, 1] })
        : null
    observer?.observe(el)

    const interval = window.setInterval(schedule, 250)

    return () => {
      window.removeEventListener('scroll', schedule)
      window.removeEventListener('resize', schedule)
      observer?.disconnect()
      window.clearInterval(interval)
      if (frame !== 0) cancelAnimationFrame(frame)
    }
  }, [fullHeroRef, fixedElementPushdown])

  return compactOpen
}
